import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PROJECT_STATUSES, type ProjectStatus } from "@releve/shared";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "@/hooks/useToast";
import { parseLocalISODate, formatLocalISODate } from '@/lib/dateUtils';
import { SearchableSelect } from "@/components/SearchableSelect";
import { PersonSelect } from "@/components/people/PersonSelect";
import { useTranslation } from "@/hooks/useTranslation";
import { usePlaceIndex } from "@/hooks/usePlaces";
import { authedPost } from "@/lib/api";
import { captureException } from "@/lib/sentry";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useCreateProject } from "@/hooks/useCreateProject";
import { useUpdateProject } from "@/hooks/useUpdateProject";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { getCurrencySymbol } from "@/lib/currency";
import type { Project } from "@/hooks/useProjects";
import { DualScopeSelect } from "@/components/common/DualScopeSelect";

const formSchema = z.object({
  name: z.string().min(1, "Project name is required").max(100, "Project name too long"),
  description: z.string().optional(),
  // The DB CHECK vocabulary (`projects_status_check`) — the legacy form offered
  // `on_hold`, which the DB rejected, and never offered paused/cancelled.
  status: z.enum(PROJECT_STATUSES),
  budget: z.number().min(0).optional(),
  start_date: z.date().optional(),
  end_date: z.date().optional(),
  project_type_id: z.string().min(1, "Project type is required"),
  artistic_director_id: z.string().optional(),
  // Sustainability fields
  sustainability_score: z.number().min(0).max(100).optional(),
  carbon_footprint_level: z.enum(["low", "medium", "high", "net_zero"]).optional(),
  local_sourcing_percentage: z.number().min(0).max(100).optional(),
  sustainability_notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

/**
 * The 13 fields this dialog reads in edit mode — narrowed so the Projects
 * page can feed it straight from an index row (`toDialogProject`, no detail
 * fetch). `status` is nullable (the column allows NULL; the form maps it to
 * `planning`). A full `Project` (ProjectDetails) still satisfies it.
 */
export type DialogProject = Omit<
  Pick<
    Project,
    | "id"
    | "name"
    | "description"
    | "status"
    | "budget"
    | "start_date"
    | "end_date"
    | "project_type_id"
    | "artistic_director_id"
    | "sustainability_score"
    | "carbon_footprint_level"
    | "local_sourcing_percentage"
    | "sustainability_notes"
  >,
  "status"
> & { status: string | null };

const KNOWN_STATUSES: ReadonlySet<string> = new Set(PROJECT_STATUSES);

/** NULL (or any token outside the CHECK vocabulary) opens as `planning`. */
function formStatus(status: string | null): ProjectStatus {
  return status && KNOWN_STATUSES.has(status) ? (status as ProjectStatus) : "planning";
}

interface CreateProjectDialogProps {
  trigger?: React.ReactNode;
  project?: DialogProject | null; // If provided, we're editing
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onProjectCreated?: () => void;
  /** On successful create, navigate to the new project's detail page (default true).
   *  Pass false for create-to-select flows (e.g. picking a project for an assessment). */
  navigateOnCreate?: boolean;
}

export function CreateProjectDialog({ trigger, project, open: controlledOpen, onOpenChange, onProjectCreated, navigateOnCreate = true }: CreateProjectDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const isEditing = !!project;
  // M5.7: projects link to EXISTING places (project_places) — the legacy
  // geocode-an-address-into-project_locations flow is gone. New venues are
  // created in /resources/places first.
  const [pendingPlaceId, setPendingPlaceId] = useState<string | null>(null);
  // Only the create-only Venue section (keys shared with ProjectPlacesTab under
  // `places.*`) and the status options (`status.*`, shared with the Projects
  // grid) are translated — the rest of this dialog is historically hardcoded EN.
  const { t } = useTranslation("projects");
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  // Only fetched while the dialog is open in create mode (the picker's
  // options); the index is the same cached query the places grid uses.
  const { data: placeOptionsRows = [] } = usePlaceIndex({ enabled: open && !isEditing });
  const placeOptions = useMemo(
    () =>
      placeOptionsRows
        // Archived places are not link targets (post-merge review m7).
        .filter((p) => p.is_active)
        .map((p) => ({
          value: p.id,
          label: `${p.name} (${p.code})`,
        })),
    [placeOptionsRows],
  );
  const orgNavigate = useOrgNavigate();
  const { createProject, isLoading: isCreating } = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const isLoading = isCreating || updateProjectMutation.isPending;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      status: "planning",
    },
  });

  // Sync the form when the dialog opens (or when editing a different record). Keyed on
  // project?.id — NOT the project object — so a parent that re-renders with a freshly
  // built object of the same record (e.g. the Projects page's index row) can't clobber
  // in-progress edits.
  // Resetting on open (rather than only on prop change) also gives a clean form on every
  // reopen, even after a prior close was discarded.
  useEffect(() => {
    if (!open) return;
    if (project) {
      form.reset({
        name: project.name,
        description: project.description || "",
        status: formStatus(project.status),
        budget: project.budget ?? undefined,
        start_date: project.start_date ? parseLocalISODate(project.start_date) : undefined,
        end_date: project.end_date ? parseLocalISODate(project.end_date) : undefined,
        project_type_id: project.project_type_id || "",
        artistic_director_id: project.artistic_director_id || "",
        sustainability_score: project.sustainability_score ?? undefined,
        carbon_footprint_level: (project.carbon_footprint_level ?? undefined) as FormData["carbon_footprint_level"],
        local_sourcing_percentage: project.local_sourcing_percentage ?? undefined,
        sustainability_notes: project.sustainability_notes ?? "",
      });
    } else {
      form.reset({
        name: "",
        description: "",
        status: "planning",
        budget: undefined,
        start_date: undefined,
        end_date: undefined,
        project_type_id: "",
        artistic_director_id: "",
        sustainability_score: undefined,
        carbon_footprint_level: undefined,
        local_sourcing_percentage: undefined,
        sustainability_notes: "",
      });
      setPendingPlaceId(null);
    }
    // Reset only when the dialog opens or the edited record's id changes; `project` and
    // `form` are intentionally omitted from the deps (see the note above the effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);


  const onSubmit = async (data: FormData) => {
    if (isEditing && project) {
      // Update existing project — route through the shared hook (it invalidates the
      // project + projects queries; updated_at is maintained by a DB trigger).
      try {
        await updateProjectMutation.mutateAsync({
          id: project.id,
          name: data.name,
          description: data.description,
          status: data.status,
          // Cleared numeric fields arrive as undefined; send null so the update
          // actually persists the clear (the hook skips undefined fields).
          budget: data.budget ?? null,
          // projects.start_date/end_date are plain date columns: send the local
          // calendar day, never an instant (docs/DATES.md).
          start_date: data.start_date ? formatLocalISODate(data.start_date) : null,
          end_date: data.end_date ? formatLocalISODate(data.end_date) : null,
          project_type_id: data.project_type_id,
          artistic_director_id: data.artistic_director_id || null,
          sustainability_score: data.sustainability_score ?? null,
          carbon_footprint_level: data.carbon_footprint_level,
          local_sourcing_percentage: data.local_sourcing_percentage ?? null,
          sustainability_notes: data.sustainability_notes,
        });

        toast.success('Project updated successfully');
        // Edit closes in place — the user is already looking at the record.
        setOpen(false);
        onProjectCreated?.();
      } catch (error) {
        // useUpdateProject does not toast on error, so surface it here.
        console.error('Error updating project:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to update project');
      }
    } else {
      // Create new project
      try {
        const newProject = await createProject({
          name: data.name,
          description: data.description,
          status: data.status,
          budget: data.budget,
          start_date: data.start_date,
          end_date: data.end_date,
          project_type_id: data.project_type_id,
          artistic_director_id: data.artistic_director_id || null,
          sustainability_score: data.sustainability_score,
          carbon_footprint_level: data.carbon_footprint_level,
          local_sourcing_percentage: data.local_sourcing_percentage,
          sustainability_notes: data.sustainability_notes,
        });

        // Fire-and-forget the pending place link: it's not aborted on unmount;
        // the detail page's own queries fetch fresh once it lands. Primary by
        // construction — it's the project's first (and only) link.
        if (pendingPlaceId && newProject && selectedOrganization) {
          void authedPost(
            `/api/v1/places/orgs/${selectedOrganization.id}/projects/${newProject.id}/places`,
            { place_id: pendingPlaceId, role: "venue", is_primary: true },
          )
            // The link changes the new row's `places` (map pin, place column,
            // search text) — refresh the projects index once it lands. Not
            // chained: a refetch hiccup must never read as a failed link.
            .then(() => {
              void queryClient.invalidateQueries({
                queryKey: ["projects", selectedOrganization.id],
              });
            })
            .catch((linkError: Error) => {
              // Non-fatal for the user flow (the project exists; the place can
              // be linked from its Places tab) but never silent for the USER
              // either (post-merge review M3): the standalone sonner toast is
              // mounted at the App root, so it survives the orgNavigate below —
              // no race. OBSERVABILITY.md: toast AND trace.
              toast.error(t("places.linkAfterCreateFailed"));
              captureException(linkError, {
                context: "CreateProjectDialog.linkPlaceAfterCreate",
              });
            });
        }

        form.reset();
        setPendingPlaceId(null);

        // Continue to the new project's detail page (unless the caller opted out, e.g. a
        // create-to-select flow). navigate(replace) must run BEFORE any close — including
        // onProjectCreated, which closes the dialog for some callers (QuickAddPanel) — so
        // replace clears the dialog's history marker and the wrapper skips its history.back().
        if (navigateOnCreate && newProject?.code) {
          orgNavigate(`/projects/${newProject.code}`, { replace: true });
        }
        onProjectCreated?.();
        setOpen(false);
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  return (
    <>
      {!isEditing && controlledOpen === undefined && (
        <div onClick={() => setOpen(true)}>
          {trigger || (
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </Button>
          )}
        </div>
      )}

      <FullscreenFormDialog
        open={open}
        onOpenChange={setOpen}
        title={isEditing ? "Edit Project" : "Create New Project"}
        description={isEditing ? "Edit project details" : "Create a new project"}
        onSubmit={form.handleSubmit(onSubmit)}
        submitLabel={isEditing ? "Save Changes" : "Create Project"}
        submittingLabel={isEditing ? "Saving…" : "Creating…"}
        isSubmitting={isLoading}
        skipCloseConfirmation={!form.formState.isDirty && !pendingPlaceId}
        formId="create-project-form"
      >
        <Form {...form}>
          <div className="space-y-8">
            {/* Metadata row — internal associations, above the document card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="project_type_id"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Project Type</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <FormControl>
                      <DualScopeSelect
                        tableName="project_types"
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select project type"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Status</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                          <SelectValue placeholder="Select project status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PROJECT_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {t(`status.${status}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="artistic_director_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("artisticDirector.label")}</FormLabel>
                    <FormControl>
                      {/* A `people` id. `quick`: this host is a
                          FullscreenFormDialog, which must not open a second
                          fullscreen dialog. */}
                      <PersonSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={t("artisticDirector.placeholder")}
                        createMode="quick"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Document body */}
            <Card>
              <CardContent className="pt-6 space-y-8">
                {/* Hero + DETAILS */}
                <div>
                  <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                    Project
                  </h2>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Project Name
                            </FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Input
                              placeholder="Enter project name..."
                              className={fieldState.error ? "border-destructive" : ""}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Description
                            </FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Textarea
                              placeholder="Describe the project goals and objectives..."
                              className={`resize-none ${fieldState.error ? "border-destructive" : ""}`}
                              rows={3}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="budget"
                      render={({ field, fieldState }) => {
                        const currencySymbol = getCurrencySymbol(selectedOrganization?.currency || 'EUR');
                        return (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Budget
                              </FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                                  {currencySymbol}
                                </span>
                                <NumberInput
                                  min={0}
                                  step={0.01}
                                  allowEmpty
                                  placeholder="0.00"
                                  className={`pl-8 ${fieldState.error ? "border-destructive" : ""}`}
                                  {...field}
                                  onChange={(value) => field.onChange(value === '' ? undefined : Number(value))}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />
                  </div>
                </div>

                {/* PLACE (create-only) — link the venue as the project's
                    primary place on creation (M5.7: existing places only; new
                    venues are created under Resources → Places first). */}
                {!isEditing && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      {/* The link is created with role "venue" — reuse that role's
                          label so the heading can't drift from the data. */}
                      {t("places.roles.venue")}
                    </h3>
                    {/* Every sibling field in this dialog is a FormField +
                        FormLabel; this one is not form state (it drives a
                        post-create side effect), so it carries its own
                        associated Label rather than going label-less with
                        only a section eyebrow and a placeholder. */}
                    <Label htmlFor="project-venue-picker" className="mb-1.5 block">
                      {t("places.place")}
                    </Label>
                    <SearchableSelect
                      id="project-venue-picker"
                      options={placeOptions}
                      value={pendingPlaceId ?? undefined}
                      onValueChange={(id) => setPendingPlaceId(id || null)}
                      placeholder={t("places.linkPlaceOptional")}
                      searchPlaceholder={t("places.searchPlaces")}
                      emptyText={t("places.noPlacesCreateHere")}
                    />
                    {pendingPlaceId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => setPendingPlaceId(null)}
                      >
                        {t("places.remove")}
                      </Button>
                    )}
                  </div>
                )}

                {/* SUSTAINABILITY GOALS — the single full-bleed band. Theme-aware bg-muted
                    (not the frozen paper look) because it contains theme-aware NumberInputs. */}
                <div className="-mx-6 px-6 py-6 bg-muted space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Sustainability Goals
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="sustainability_score"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Target Sustainability Score (0-100)
                            </FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <NumberInput
                              min={0}
                              max={100}
                              step={1}
                              placeholder="75"
                              allowEmpty
                              className={fieldState.error ? "border-destructive" : ""}
                              {...field}
                              onChange={(value) => field.onChange(value === '' ? undefined : Number(value))}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="carbon_footprint_level"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Carbon Footprint Goal
                            </FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                                <SelectValue placeholder="Select target level" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="low">Low Impact</SelectItem>
                              <SelectItem value="medium">Medium Impact</SelectItem>
                              <SelectItem value="high">High Impact</SelectItem>
                              <SelectItem value="net_zero">Net Zero</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="local_sourcing_percentage"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Target Local Sourcing (%)
                          </FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <NumberInput
                            min={0}
                            max={100}
                            step={1}
                            placeholder="60"
                            allowEmpty
                            className={fieldState.error ? "border-destructive" : ""}
                            {...field}
                            onChange={(value) => field.onChange(value === '' ? undefined : Number(value))}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sustainability_notes"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Sustainability Notes
                          </FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <Textarea
                            placeholder="Describe your sustainability goals and priorities for this project..."
                            className={`resize-none ${fieldState.error ? "border-destructive" : ""}`}
                            rows={2}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                {/* SCHEDULE */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Schedule
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="start_date"
                      render={({ field, fieldState }) => (
                        <FormItem className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Start Date
                            </FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              className={fieldState.error ? "border-destructive" : ""}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="end_date"
                      render={({ field, fieldState }) => (
                        <FormItem className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              End Date
                            </FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              className={fieldState.error ? "border-destructive" : ""}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </Form>
      </FullscreenFormDialog>
    </>
  );
}
